import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faHourglass } from '@fortawesome/pro-solid-svg-icons';
import { differenceInHours, isFuture, isPast } from 'date-fns';
import { FormattedMessage, defineMessages } from 'react-intl';

import { ReferralRewardState, Sport } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS } from '#src/atoms/typography/index.tsx';
import { Countdown } from '#src/components/Countdown/index.tsx';
import { ClaimReferralRewardsDialog } from '#src/components/referral/ClaimReferralRewardsDialog/index.tsx';
import { ANY_SPORT_PLAY } from '#src/constants/__generated__/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { TimeLeft } from '#src/contexts/ticker/TimeLeft/index.tsx';
import { fantasy, glossary } from '#src/lib/glossary.ts';
import { generateSportPath } from '#src/lib/routing/generateSportPath.ts';

import type { ReadyToClaimReferralBar_ReferralReward } from './__generated__/index.graphql.ts';

const messages = defineMessages({
  playTwoMoreGW: {
    id: 'Referral.ReadyToClaimReferralBar.twoMissingGW2',
    defaultMessage:
      'Play 2 Game Weeks with the cards you bought to unlock your reward. {gift}',
  },
  playTwoMore: {
    id: 'Referral.ReadyToClaimReferralBar.twoMissing',
    defaultMessage:
      'Play twice with the cards you bought to unlock your reward. {gift}',
  },
  playOneMoreGW: {
    id: 'Referral.ReadyToClaimReferralBar.oneMissingGW2',
    defaultMessage:
      'Play 1 more Game Week with the cards you bought to unlock your reward. {gift}',
  },
  playOneMore: {
    id: 'Referral.ReadyToClaimReferralBar.oneMissing',
    defaultMessage:
      'Play one more time with the cards you bought to unlock your reward. {gift}',
  },
});

const MightClaim = ({
  refereeRewards,
}: {
  refereeRewards: ReadyToClaimReferralBar_ReferralReward[];
}) => {
  const { sport } = useSportContext();
  const claimableAt = refereeRewards
    .map(r => r.claimableAt)
    .filter(Boolean)
    .sort()[0];

  const imminentClaim =
    claimableAt &&
    isFuture(claimableAt) &&
    differenceInHours(claimableAt, new Date()) < 48;

  const oneMissingGW = refereeRewards.some(
    r => r.claimableState === ReferralRewardState.NOT_CLAIMABLE_YET_1_GW_MISSING
  );

  const twoMissingGW = refereeRewards.some(
    r => r.claimableState === ReferralRewardState.NOT_CLAIMABLE_YET_2_GW_MISSING
  );

  const compose = (
    <>
      <Button
        size="medium"
        color="primary"
        to={generateSportPath(ANY_SPORT_PLAY, { sport })}
        name="compose"
      >
        <FormattedMessage {...fantasy.composeLineup} />
      </Button>
      <BodyS color="var(--c-nd-600)" className="text-center">
        <FontAwesomeIcon icon={faHourglass} />{' '}
        <FormattedMessage
          id="Referral.ReadyToClaimReferralBar.timeLeft"
          defaultMessage="or wait {timeLeft}"
          values={{
            timeLeft: <TimeLeft time={claimableAt} />,
          }}
        />
      </BodyS>
    </>
  );

  if (oneMissingGW && !imminentClaim) {
    return (
      <>
        <BodyM>
          <FormattedMessage
            {...(sport === Sport.NBA
              ? messages.playOneMore
              : messages.playOneMoreGW)}
            values={{ gift: '🎁' }}
          />
        </BodyM>
        {compose}
      </>
    );
  }
  if (twoMissingGW && !imminentClaim) {
    return (
      <>
        <BodyM>
          <FormattedMessage
            {...(sport === Sport.NBA
              ? messages.playTwoMore
              : messages.playTwoMoreGW)}
            values={{ gift: '🎁' }}
          />
        </BodyM>
        {compose}
      </>
    );
  }
  return (
    <>
      <BodyM>
        <FormattedMessage
          id="Referral.ReadyToClaimReferralBar.claimableSoon2"
          defaultMessage="Your reward will be claimable soon."
        />
      </BodyM>
      <Countdown date={claimableAt} />
      <Button size="medium" color="primary" disabled>
        <FormattedMessage {...glossary.claim} />
      </Button>
    </>
  );
};

type Props = {
  refereeRewards: Array<ReadyToClaimReferralBar_ReferralReward>;
  openClaimDialog: boolean;
  toggleOpenClaimDialog: () => void;
};

export const ReadyToClaimReferralBar = ({
  refereeRewards,
  openClaimDialog,
  toggleOpenClaimDialog,
}: Props) => {
  const atLeastOneRewardIsClaimable = refereeRewards.some(
    r => r.claimableState === ReferralRewardState.CLAIMABLE
  );

  const claimableAt = refereeRewards
    .map(r => r.claimableAt)
    .filter(Boolean)
    .sort()[0];

  return (
    <>
      <Vertical>
        {!!(
          atLeastOneRewardIsClaimable ||
          // If the reward has just become claimable, the state hasn't been updated yet
          (claimableAt && isPast(claimableAt))
        ) && (
          <>
            <BodyM>
              <FormattedMessage
                id="Referral.ReadyToClaimReferralBar.yourRefereeRewardIsWaitingForYou"
                defaultMessage="Your referee reward is waiting for you! {gift}"
                values={{ gift: '🎁' }}
              />
            </BodyM>
            <Button
              size="medium"
              color="primary"
              onClick={toggleOpenClaimDialog}
              name="claim"
            >
              <FormattedMessage {...glossary.claim} />
            </Button>
          </>
        )}
        {!!claimableAt && isFuture(claimableAt) && (
          <MightClaim refereeRewards={refereeRewards} />
        )}
      </Vertical>
      <ClaimReferralRewardsDialog
        referralRewards={refereeRewards}
        open={openClaimDialog}
        onClose={toggleOpenClaimDialog}
      />
    </>
  );
};

ReadyToClaimReferralBar.fragments = {
  referralReward: gql`
    fragment ReadyToClaimReferralBar_ReferralReward on ReferralReward {
      id
      claimableState
      claimableAt
      ...ClaimReferralRewardsDialog_referralReward
    }
    ${ClaimReferralRewardsDialog.fragments.referralReward}
  ` as TypedDocumentNode<ReadyToClaimReferralBar_ReferralReward>,
};
